import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { newsAPI, binderAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
    Newspaper, 
    ExternalLink, 
    FileText, 
    Video, 
    BookOpen,
    Calendar,
    RefreshCw,
    Rss,
    Bookmark,
    FolderOpen,
    Loader2,
    Plus
} from 'lucide-react';

const NEWS_CATEGORIES = ["All", "Reports", "Articles", "Videos", "Press Releases", "Research", "Events", "Other"];

const CATEGORY_ICONS = {
    "Reports": FileText,
    "Articles": BookOpen,
    "Videos": Video,
    "Press Releases": Newspaper,
    "Research": BookOpen,
    "Events": Calendar,
    "Other": FileText,
};

const NewsResourcesPage = () => {
    const { isAuthenticated } = useAuth();
    const [news, setNews] = useState([]);
    const [googleNews, setGoogleNews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingGoogleNews, setLoadingGoogleNews] = useState(true);
    const [activeCategory, setActiveCategory] = useState('All');

    // Save to binder state
    const [showSaveToBinder, setShowSaveToBinder] = useState(false);
    const [saveTarget, setSaveTarget] = useState(null);
    const [binders, setBinders] = useState([]);
    const [selectedBinder, setSelectedBinder] = useState('');
    const [saveNotes, setSaveNotes] = useState('');
    const [savingToBinder, setSavingToBinder] = useState(false);
    const [loadingBinders, setLoadingBinders] = useState(false);

    useEffect(() => {
        fetchNews();
        fetchGoogleNews();
    }, []);

    const fetchNews = async () => {
        try {
            const response = await newsAPI.getAll({ limit: 50 });
            setNews(response.data.news || []);
        } catch (error) {
            console.error('Error fetching news:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchGoogleNews = async () => {
        try {
            const rssUrl = 'https://news.google.com/rss/search?q=neurodiversity+OR+autism+employment+OR+autistic+workplace&hl=en-US&gl=US&ceid=US:en';
            const proxyUrl = `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(rssUrl)}`;
            const response = await fetch(proxyUrl);
            const data = await response.json();
            if (data.status === 'ok' && data.items) {
                setGoogleNews(data.items.slice(0, 10));
            }
        } catch (error) {
            console.error('Error fetching Google News:', error);
        } finally {
            setLoadingGoogleNews(false);
        }
    };

    const filteredNews = activeCategory === 'All' 
        ? news 
        : news.filter(item => item.category === activeCategory);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const handleSaveArticle = async (item, isGoogleNews = false) => {
        setSaveTarget({
            title: item.title,
            url: isGoogleNews ? item.link : (item.url || item.file_url),
            source: isGoogleNews ? (item.author || 'Google News') : (item.source || 'HAAPE'),
            category: isGoogleNews ? 'News' : (item.category || ''),
            description: item.description || '',
            preview_image: isGoogleNews ? null : item.preview_image,
        });
        setSelectedBinder('');
        setSaveNotes('');
        setShowSaveToBinder(true);
        if (binders.length === 0) {
            setLoadingBinders(true);
            try {
                const res = await binderAPI.getBinders();
                setBinders(res.data || []);
            } catch {
                setBinders([]);
            } finally {
                setLoadingBinders(false);
            }
        }
    };

    const confirmSave = async () => {
        if (!selectedBinder || !saveTarget) return;
        setSavingToBinder(true);
        try {
            await binderAPI.saveBookmark(selectedBinder, {
                ...saveTarget,
                notes: saveNotes || undefined
            });
            toast.success('Saved to binder!');
            setShowSaveToBinder(false);
        } catch (error) {
            if (error.response?.status === 409) {
                toast.info('Already saved to that binder');
            } else {
                toast.error(error.response?.data?.detail || 'Failed to save');
            }
        } finally {
            setSavingToBinder(false);
        }
    };

    return (
        <div className="min-h-screen bg-background" data-testid="news-resources-page">
            {/* Header */}
            <div className="bg-card border-b border-border">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <h1 className="font-heading text-3xl font-bold mb-2 flex items-center gap-3">
                        <Newspaper className="h-8 w-8 text-primary" />
                        Resource Library
                    </h1>
                    <p className="text-muted-foreground">
                        Stay informed with the latest news, reports, and resources on neurodiversity
                    </p>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Main Content - HAAPE Resources */}
                    <div className="lg:col-span-2">
                        <Card>
                            <CardHeader>
                                <CardTitle>HAAPE Resources</CardTitle>
                                <CardDescription>
                                    Curated resources from HAAPE and our partners
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                {/* Category Tabs */}
                                <Tabs value={activeCategory} onValueChange={setActiveCategory} className="mb-6">
                                    <TabsList className="flex flex-wrap h-auto gap-1">
                                        {NEWS_CATEGORIES.map((cat) => (
                                            <TabsTrigger key={cat} value={cat} className="text-xs">
                                                {cat}
                                            </TabsTrigger>
                                        ))}
                                    </TabsList>
                                </Tabs>

                                {/* Resources List */}
                                {loading ? (
                                    <div className="space-y-4">
                                        {[...Array(5)].map((_, i) => (
                                            <div key={i} className="flex gap-4 p-4 border border-border rounded-lg">
                                                <Skeleton className="h-16 w-16 rounded shrink-0" />
                                                <div className="flex-1">
                                                    <Skeleton className="h-5 w-3/4 mb-2" />
                                                    <Skeleton className="h-4 w-1/2" />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : filteredNews.length === 0 ? (
                                    <div className="text-center py-12">
                                        <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                                        <h3 className="font-heading text-lg font-semibold mb-2">No Resources Yet</h3>
                                        <p className="text-muted-foreground">
                                            {activeCategory === 'All' 
                                                ? 'Check back soon for new resources' 
                                                : `No ${activeCategory.toLowerCase()} available yet`}
                                        </p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {filteredNews.map((item) => {
                                            const CategoryIcon = CATEGORY_ICONS[item.category] || FileText;
                                            return (
                                                <div
                                                    key={item.news_id}
                                                    className="flex gap-4 p-4 border border-border rounded-lg hover:border-primary/50 hover:bg-muted/50 transition-all duration-200 group"
                                                    data-testid={`news-item-${item.news_id}`}
                                                >
                                                    {item.preview_image ? (
                                                        <img 
                                                            src={item.preview_image} 
                                                            alt={item.title}
                                                            className="h-20 w-20 object-cover rounded shrink-0"
                                                        />
                                                    ) : (
                                                        <div className="h-20 w-20 bg-primary/10 rounded flex items-center justify-center shrink-0">
                                                            <CategoryIcon className="h-8 w-8 text-primary" />
                                                        </div>
                                                    )}
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-start justify-between gap-2">
                                                            <a
                                                                href={item.url || item.file_url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="font-semibold group-hover:text-primary transition-colors line-clamp-2 flex-1"
                                                            >
                                                                {item.title}
                                                            </a>
                                                            <div className="flex items-center gap-1 shrink-0">
                                                                {isAuthenticated && (
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-8 w-8 text-muted-foreground hover:text-primary"
                                                                        onClick={() => handleSaveArticle(item)}
                                                                        title="Save to Binder"
                                                                        data-testid={`save-news-${item.news_id}`}
                                                                    >
                                                                        <Bookmark className="h-4 w-4" />
                                                                    </Button>
                                                                )}
                                                                <a href={item.url || item.file_url} target="_blank" rel="noopener noreferrer">
                                                                    <ExternalLink className="h-4 w-4 text-muted-foreground" />
                                                                </a>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                            <Badge variant="secondary" className="text-xs">
                                                                {item.category}
                                                            </Badge>
                                                            <span className="text-xs text-muted-foreground">
                                                                {item.source}
                                                            </span>
                                                            {item.contributed_by_name && (
                                                                <span className="text-xs text-muted-foreground">
                                                                    Posted by <strong>{item.contributed_by_name}</strong>
                                                                </span>
                                                            )}
                                                            {item.created_at && (
                                                                <span className="text-xs text-muted-foreground">
                                                                    {new Date(item.created_at).toLocaleDateString()}
                                                                </span>
                                                            )}
                                                            {item.contributed_by && (
                                                                <a 
                                                                    href={`mailto:${item.contributed_by}?subject=Question about: ${item.title}`}
                                                                    className="text-xs text-primary hover:underline"
                                                                >
                                                                    Contact poster
                                                                </a>
                                                            )}
                                                        </div>
                                                        {item.description && (
                                                            <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                                                                {item.description}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Sidebar - Google News Feed */}
                    <div>
                        <Card className="sticky top-4">
                            <CardHeader className="pb-3">
                                <CardTitle className="flex items-center justify-between">
                                    <span className="flex items-center gap-2">
                                        <Rss className="h-5 w-5 text-primary" />
                                        Neurodiversity News
                                    </span>
                                    <Button 
                                        variant="ghost" 
                                        size="icon"
                                        onClick={() => {
                                            setLoadingGoogleNews(true);
                                            fetchGoogleNews();
                                        }}
                                        disabled={loadingGoogleNews}
                                    >
                                        <RefreshCw className={`h-4 w-4 ${loadingGoogleNews ? 'animate-spin' : ''}`} />
                                    </Button>
                                </CardTitle>
                                <CardDescription>
                                    Live feed from Google News
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                {loadingGoogleNews ? (
                                    <div className="space-y-4">
                                        {[...Array(5)].map((_, i) => (
                                            <div key={i}>
                                                <Skeleton className="h-4 w-full mb-2" />
                                                <Skeleton className="h-3 w-2/3" />
                                            </div>
                                        ))}
                                    </div>
                                ) : googleNews.length === 0 ? (
                                    <p className="text-sm text-muted-foreground text-center py-4">
                                        Unable to load news feed
                                    </p>
                                ) : (
                                    <div className="space-y-4">
                                        {googleNews.map((item, index) => (
                                            <div key={index} className="pb-4 border-b border-border last:border-0 last:pb-0 group">
                                                <div className="flex items-start justify-between gap-2">
                                                    <a
                                                        href={item.link}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-sm font-medium group-hover:text-primary transition-colors line-clamp-2 flex-1"
                                                    >
                                                        {item.title}
                                                    </a>
                                                    {isAuthenticated && (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-7 w-7 text-muted-foreground hover:text-primary shrink-0"
                                                            onClick={() => handleSaveArticle(item, true)}
                                                            title="Save to Binder"
                                                            data-testid={`save-gnews-${index}`}
                                                        >
                                                            <Bookmark className="h-3.5 w-3.5" />
                                                        </Button>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <span className="text-xs text-muted-foreground truncate">
                                                        {item.author || 'Google News'}
                                                    </span>
                                                    <span className="text-xs text-muted-foreground">•</span>
                                                    <span className="text-xs text-muted-foreground">
                                                        {formatDate(item.pubDate)}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="mt-4 pt-4 border-t border-border">
                                    <a 
                                        href="https://news.google.com/search?q=neurodiversity%20autism%20employment"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm text-primary hover:underline flex items-center gap-1"
                                    >
                                        View more on Google News
                                        <ExternalLink className="h-3 w-3" />
                                    </a>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>

            {/* Save to Binder Dialog */}
            <Dialog open={showSaveToBinder} onOpenChange={setShowSaveToBinder}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FolderOpen className="h-5 w-5 text-primary" />
                            Save to Binder
                        </DialogTitle>
                        <DialogDescription>
                            Save "{saveTarget?.title}" to one of your digital binders.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        {loadingBinders ? (
                            <div className="flex items-center justify-center py-4">
                                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                            </div>
                        ) : binders.length === 0 ? (
                            <div className="text-center py-4" data-testid="no-binders-news">
                                <FolderOpen className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
                                <p className="text-sm text-muted-foreground mb-3">You don't have any binders yet.</p>
                                <Button size="sm" variant="outline" asChild>
                                    <a href="/binder"><Plus className="h-4 w-4 mr-1" />Create a Binder</a>
                                </Button>
                            </div>
                        ) : (
                            <>
                                <div className="space-y-2">
                                    <Label>Select Binder</Label>
                                    <Select value={selectedBinder} onValueChange={setSelectedBinder}>
                                        <SelectTrigger data-testid="news-binder-select">
                                            <SelectValue placeholder="Choose a binder..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {binders.map(b => (
                                                <SelectItem key={b.binder_id} value={b.binder_id}>
                                                    {b.name}{b.loved_one_name ? ` (${b.loved_one_name})` : ''}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>Notes (optional)</Label>
                                    <Textarea
                                        placeholder="Why are you saving this article?"
                                        rows={2}
                                        value={saveNotes}
                                        onChange={e => setSaveNotes(e.target.value)}
                                        data-testid="news-save-notes"
                                    />
                                </div>
                            </>
                        )}
                    </div>
                    {binders.length > 0 && (
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setShowSaveToBinder(false)}>Cancel</Button>
                            <Button onClick={confirmSave} disabled={savingToBinder || !selectedBinder} data-testid="news-confirm-save">
                                {savingToBinder ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Bookmark className="h-4 w-4 mr-2" />}
                                Save to Binder
                            </Button>
                        </DialogFooter>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default NewsResourcesPage;
